import type { ReactNode } from 'react'
import { HintDot } from '../help/HintDot'
import type { HintId } from '../help/hints'
import { swatchBackground, type OverlaySwatch } from './overlaySwatch'

/**
 * Единый каркас раздела-оверлея в панели: заголовок + образец цвета + тумблер,
 * под ними — панель настроек, пока оверлей включён.
 *
 * До этого «Изотерма», «Фронты» и «Волнение» были собраны тремя разными способами
 * (у волнения тумблер стоял слева и весь заголовок был кнопкой) — соседние по
 * смыслу инструменты выглядели как разные сущности. Здесь форма одна, различает их
 * ровно образец цвета: по нему видно, какой линией лягут данные на карту, когда
 * включены сразу несколько (фидбек владельца 2026-08-07).
 */
interface Props {
  title: string
  /** Подсказка «i» у заголовка — что именно ляжет на карту. */
  hint: HintId
  swatch: OverlaySwatch
  enabled: boolean
  /** Подпись тумблера для скринридера — «Показывать …». */
  toggleLabel: string
  onToggle: (on: boolean) => void
  children?: ReactNode
}

export function OverlaySection({
  title,
  hint,
  swatch,
  enabled,
  toggleLabel,
  onToggle,
  children,
}: Props) {
  return (
    <div>
      <div className="eyebrow mb-3 flex items-center gap-1.5">
        {title}
        <HintDot id={hint} />
        {/* Образец — то, чем будет раскрашена карта; выключенный приглушён, но
            остаётся читаемым: по нему и выбирают, что включить. */}
        <span
          aria-hidden
          className={
            'ml-auto mr-3 h-2 w-16 rounded-full transition-opacity ' +
            (enabled ? '' : 'opacity-40')
          }
          style={{ background: swatchBackground(swatch) }}
        />
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={toggleLabel}
          onClick={() => onToggle(!enabled)}
          className={
            'relative h-4 w-7 flex-shrink-0 rounded-full transition-colors ' +
            (enabled ? 'bg-sonar' : 'bg-hull')
          }
        >
          <span
            className="absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all"
            style={{ left: enabled ? 14 : 2 }}
          />
        </button>
      </div>

      {enabled && children && (
        <div className="rounded-lg border border-hull bg-trench px-3 py-2.5">{children}</div>
      )}
    </div>
  )
}
